import React, { useState, useEffect, useCallback } from "react";
import api from "../utils/api";

// Add CSS for animations
const animationStyles = `
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const GameTable = (props) => {
  // Add style element for animations
  useEffect(() => {
    const styleElement = document.createElement("style");
    styleElement.innerHTML = animationStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  // State variables for table data, total records, pagination, search and loading state.
  const [totalRows, setTotalRows] = useState(0);
  const [perPage, setPerPage] = useState(9); // Changed to 9 for a 3x3 grid
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");

  function getStartPosition(pageNumber, recordsPerPage) {
    return (pageNumber - 1) * recordsPerPage;
  }

  const fetchGames = useCallback(async () => {
    setLoading(true);
    // Build query parameters. Adjust parameter names if your backend expects different keys.
    const queryParams = new URLSearchParams();
    queryParams.append("start", getStartPosition(page, perPage));
    queryParams.append("length", perPage);
    queryParams.append("search[value]", search);

    try {
      const response = await api.get(`/api/game/gametable.php?${queryParams}`);
      console.log("Response:", response.data);
      setData(Array.isArray(response.data.data) ? response.data.data : []);
      setTotalRows(response.data.recordsFiltered);
    } catch (error) {
      console.error("Error fetching games:", error);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, search]);

  useEffect(() => {
    fetchGames();
  }, [fetchGames, page, perPage, search]);

  // Handle page change event
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change event
  const handlePerRowsChange = (newPerPage) => {
    setPerPage(newPerPage);
    setPage(1); // Reset to first page when changing records per page
  };

  const handleEdit = (game) => {
    console.log("Edit game:", game);
    props.onEdit(game);
  };

  const handleDelete = (game) => {
    console.log("Delete game:", game);
    // Add delete functionality here
  };

  // Game Card Component
  const GameCard = ({ game, index }) => {
    // Calculate animation delay based on index
    const animationDelay = `${index * 0.1}s`;

    return (
      <div
        className="relative overflow-hidden rounded-xl shadow-xl border border-gray-200 hover:shadow-2xl transition-all duration-500 transform hover:scale-105 hover:translate-y-[-5px]"
        style={{
          opacity: 0,
          animation: `fadeInUp 0.6s ease-out ${animationDelay} forwards`,
          background: "linear-gradient(135deg, #ffffff 0%, #f5f7fa 100%)",
        }}
      >
        {/* Header with gradient */}
        <div
          className="p-4 text-center relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #2563eb 0%, #4f46e5 100%)", // Changed header color to a different blue gradient
            boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
          }}
        >
          {/* Action buttons in header */}
          <div className="absolute right-3 top-3 flex space-x-2">
            {/* Edit button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(game);
              }}
              className="w-8 h-8 flex items-center justify-center bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-all duration-300 focus:outline-none"
              title="Edit Game"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
            </button>

            {/* Delete button */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(game);
              }}
              className="w-8 h-8 flex items-center justify-center bg-white bg-opacity-20 rounded-full hover:bg-opacity-30 transition-all duration-300 focus:outline-none"
              title="Delete Game"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 text-white"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>
          </div>

          {/* Decorative circles */}
          <div className="absolute left-3 top-3 w-2 h-2 rounded-full bg-white opacity-50"></div>
          <div className="absolute left-7 top-3 w-2 h-2 rounded-full bg-white opacity-30"></div>
          <div className="absolute left-11 top-3 w-2 h-2 rounded-full bg-white opacity-10"></div>

          <h3 className="text-xl font-bold text-white tracking-wider">
            {game.gameName}
          </h3>
        </div>

        {/* Content with improved spacing and typography */}
        <div className="p-6 space-y-4">
          <div className="flex flex-col border-b border-gray-100 pb-3">
            <span className="text-gray-600 font-bold text-lg mb-1">
              அதிகாரம்:
            </span>
            <div className="ml-2">
              {game.athikaram.split(",").map((item, i) => (
                <div key={i} className="flex items-center">
                  <div className="w-2 h-2 rounded-full bg-indigo-500 mr-2"></div>
                  <span className="text-gray-800">{item.trim()}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-between items-center border-b border-gray-100 pb-1">
            <span className="text-gray-600 font-medium">Status:</span>
            <span
              className={`font-semibold ${
                game.isActive ? "text-green-600" : "text-red-600"
              }`}
            >
              {game.isActive ? "Active" : "Inactive"}
            </span>
          </div>

          {/* Game metrics with enhanced styling */}
          <div className="grid grid-cols-2 gap-4 my-4">
            <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-3 text-center transform transition-transform hover:scale-105">
              <div className="text-indigo-600 font-bold text-xl">
                {game.noOfKurals}
              </div>
              <div className="text-gray-500 text-sm">No. of Kurals</div>
            </div>
            <div className="bg-purple-50 border rounded-lg p-3 text-center transform transition-transform hover:scale-105">
              <div className="text-purple-600 font-bold text-xl">
                {game.duration} sec
              </div>
              <div className="text-gray-500 text-sm">Game Duration</div>
            </div>
          </div>

          {/* Removed the action buttons from here */}
        </div>
      </div>
    );
  };

  // Custom pagination component
  const Pagination = () => {
    const totalPages = Math.ceil(totalRows / perPage);
    const pageNumbers = [];

    // Calculate which page numbers to show
    let startPage = Math.max(1, page - 2);
    let endPage = Math.min(totalPages, page + 2);

    // Ensure we always show 5 pages if possible
    if (endPage - startPage < 4) {
      if (startPage === 1) {
        endPage = Math.min(5, totalPages);
      } else if (endPage === totalPages) {
        startPage = Math.max(1, totalPages - 4);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      <div className="flex justify-center mt-6 mb-4">
        <nav className="flex items-center space-x-1">
          <button
            onClick={() => handlePageChange(1)}
            disabled={page === 1}
            className={`px-3 py-1 rounded ${
              page === 1
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            «
          </button>
          <button
            onClick={() => handlePageChange(page - 1)}
            disabled={page === 1}
            className={`px-3 py-1 rounded ${
              page === 1
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            ‹
          </button>

          {pageNumbers.map((num) => (
            <button
              key={num}
              onClick={() => handlePageChange(num)}
              className={`px-3 py-1 rounded ${
                page === num
                  ? "bg-blue-500 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              {num}
            </button>
          ))}

          <button
            onClick={() => handlePageChange(page + 1)}
            disabled={page === totalPages}
            className={`px-3 py-1 rounded ${
              page === totalPages
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            ›
          </button>
          <button
            onClick={() => handlePageChange(totalPages)}
            disabled={page === totalPages}
            className={`px-3 py-1 rounded ${
              page === totalPages
                ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            »
          </button>
        </nav>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
          <h2 className="text-lg font-semibold text-gray-800">Game List</h2>
          <div className="relative w-full sm:w-64">
            <input
              type="text"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-200"
              placeholder="Search games..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
            />
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Loading indicator */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <>
            {/* Cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data.length > 0 ? (
                data.map((game, index) => (
                  <GameCard key={game.id} game={game} index={index} />
                ))
              ) : (
                <div className="col-span-3 text-center py-8 text-gray-500">
                  No games found. Try adjusting your search.
                </div>
              )}
            </div>

            {/* Pagination */}
            {data.length > 0 && <Pagination />}

            {/* Records info and per page selector */}
            <div className="flex flex-col md:flex-row justify-between items-center mt-4 text-sm text-gray-600">
              <div className="mb-2 md:mb-0">
                Showing {getStartPosition(page, perPage) + 1} to{" "}
                {Math.min(getStartPosition(page, perPage) + perPage, totalRows)}{" "}
                of {totalRows} records
              </div>
              <div className="flex items-center">
                <span className="mr-2">Records per page:</span>
                <select
                  className="border border-gray-300 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={perPage}
                  onChange={(e) => handlePerRowsChange(Number(e.target.value))}
                >
                  <option value={9}>9</option>
                  <option value={18}>18</option>
                  <option value={27}>27</option>
                  <option value={36}>36</option>
                </select>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default GameTable;
